import {
  IProductionDashboardAssembly,
  IProductionDashboardBay,
  IProductionDashboardBayVehicle,
  IProductionDashboardSession,
  PartAssemblyType,
  ProductionDashboardAssemblyKind,
} from "../models/ProductionDashboard";
import { isAtFlowStage } from "./flowStage";
import { isSubAssemblyUnderway, vehicleAtSubAssembly } from "./subAssemblyWork";

/**
 * Where the vehicles on one production line sit, column by column.
 *
 * Shared by the Assembly Flow lanes and the Line TV mode so the two can never disagree about which
 * station a vehicle is at. Placement never reads the clock - only colouring does - and the labels
 * are left to the caller to translate, so this stays a pure function.
 */
export interface IFlowLaneColumn {
  assembly: IProductionDashboardAssembly;
  /** 1-based position among the real assemblies in flow order; null for a stage column. */
  assemblyNumber: number | null;
  /** Vehicles placed here. In a sub-assembly column these are vehicleAtSubAssembly() views. */
  vehicles: IProductionDashboardBayVehicle[];
  /** Session-only entries (a running stage timer on a vehicle not on the lane yet); empty for assembly columns. */
  sessions: IProductionDashboardSession[];
}

export interface IFlowLane {
  /** Main assemblies and stage columns, in flow order. */
  main: IFlowLaneColumn[];
  /** Sub assemblies, in flow order. */
  sub: IFlowLaneColumn[];
  /** Vehicles placed plus sessions placed - the lane header's count. */
  inProgressCount: number;
}

/**
 * Vehicles shown at a stage on their own lane, across every lane. The shared session rows (a
 * triage shows in every lane) must not repeat them.
 */
export function flowStagedVehicleIds(bays: IProductionDashboardBay[]): Set<string> {
  return new Set(
    bays.flatMap((bay) => (bay.currentVehicles ?? []).filter(isAtFlowStage).map((v) => v.id))
  );
}

export function buildFlowLane(
  bay: IProductionDashboardBay,
  assemblies: IProductionDashboardAssembly[],
  triages: IProductionDashboardSession[],
  depollutions: IProductionDashboardSession[],
  stagedVehicleIds: Set<string>,
  busyVehicleIds?: Set<string>
): IFlowLane {
  const allVehicles = bay.currentVehicles ?? [];

  // With the stages in the flow a vehicle at Triage or Depollution sits in that column with its
  // own chip, so the busy filter - which hides a running stage timer from the assembly columns -
  // is lifted for it. The API only ever sets currentStage with the stages in the flow, so with
  // them switched off a running stage timer stays hidden here as before.
  const vehicles = allVehicles.filter((v) => !busyVehicleIds?.has(v.id) || isAtFlowStage(v));
  const byStage = new Map<ProductionDashboardAssemblyKind, IProductionDashboardBayVehicle[]>();
  allVehicles.filter(isAtFlowStage).forEach((v) => {
    if (v.currentStage == null) return;
    const list = byStage.get(v.currentStage) ?? [];
    list.push(v);
    byStage.set(v.currentStage, list);
  });

  // Sub assemblies are worked off the line in parallel with it, so a vehicle sits in a
  // sub-assembly column on the strength of that assembly's own row - its main station (the
  // current assembly) is unaffected, and one vehicle can be in a main column and a sub column at
  // once. The entry in the sub column reads the sub assembly's timer and parts.
  const bySubAssembly = new Map<string, IProductionDashboardBayVehicle[]>();
  vehicles.forEach((v) => {
    (v.assemblies ?? []).filter(isSubAssemblyUnderway).forEach((a) => {
      const list = bySubAssembly.get(a.name) ?? [];
      list.push(vehicleAtSubAssembly(v, a));
      bySubAssembly.set(a.name, list);
    });
  });

  // Completed vehicles are surfaced in the shared "Completed Today" section, so the lane only
  // shows what's still in progress - with one exception. Sub-assembly parts do not count toward
  // completion, so a vehicle can be 100% down the main line while sub-assembly work is still
  // underway: that one belongs in both places at once, Completed Today and its sub-assembly column.
  const inProgress = vehicles.filter(
    (v) =>
      v.percentComplete < 100 ||
      isAtFlowStage(v) ||
      (v.assemblies ?? []).some(isSubAssemblyUnderway)
  );

  // Main columns take the vehicles at an assembly; one at a stage is placed by byStage instead.
  const byAssembly = new Map<string, IProductionDashboardBayVehicle[]>();
  inProgress.forEach((v) => {
    if (v.percentComplete >= 100 || v.currentStage != null || !v.currentAssemblyName) return;
    const list = byAssembly.get(v.currentAssemblyName) ?? [];
    list.push(v);
    byAssembly.set(v.currentAssemblyName, list);
  });

  // Only count and place sessions for a stage that actually has a column. With the stages
  // switched off there is nowhere in the lane to show them, so they must not inflate the
  // header count either - they stay in the panels at the bottom of the page.
  const hasTriageColumn = assemblies.some(
    (a) => a.kind === ProductionDashboardAssemblyKind.Triage
  );
  const hasDepollutionColumn = assemblies.some(
    (a) => a.kind === ProductionDashboardAssemblyKind.Depollution
  );

  // Triage happens before a vehicle is committed to a line, so it isn't split per lane - every
  // in-progress triage shows in every lane's triage column. Depollution is split by line where we
  // know it, but a session with no line yet could still be heading anywhere, so it shows in every
  // lane until it's allocated. A vehicle already shown at its stage on a lane is left out of both.
  const bayTriages = hasTriageColumn
    ? triages.filter((s) => !stagedVehicleIds.has(s.vehicleId))
    : [];
  const bayDepollutions = hasDepollutionColumn
    ? depollutions.filter(
        (s) =>
          !stagedVehicleIds.has(s.vehicleId) &&
          (!s.dismantlingBayId || s.dismantlingBayId === bay.id)
      )
    : [];

  // The overall flow order is preserved; the running count drives the "Assembly N" label so sub
  // assemblies stay numbered in their true position even though they render in a separate row
  // underneath the main assemblies. Stages are skipped in that count, so adding one doesn't
  // renumber the line.
  let assemblyNumber = 0;
  const columns: IFlowLaneColumn[] = assemblies.map((assembly) => {
    if (assembly.kind !== ProductionDashboardAssemblyKind.Assembly) {
      return {
        assembly,
        assemblyNumber: null,
        vehicles: byStage.get(assembly.kind) ?? [],
        sessions:
          assembly.kind === ProductionDashboardAssemblyKind.Triage ? bayTriages : bayDepollutions,
      };
    }

    assemblyNumber += 1;
    const isSub = assembly.type === PartAssemblyType.Sub;
    return {
      assembly,
      assemblyNumber,
      vehicles: (isSub ? bySubAssembly : byAssembly).get(assembly.name) ?? [],
      sessions: [],
    };
  });

  return {
    main: columns.filter((c) => c.assembly.type !== PartAssemblyType.Sub),
    sub: columns.filter((c) => c.assembly.type === PartAssemblyType.Sub),
    inProgressCount: inProgress.length + bayTriages.length + bayDepollutions.length,
  };
}
